<template>
  <PageError v-if="error" :error="error" :errorCode="errorCode" />

  <div v-else-if="canViewProfile || user?.is_staff" :class="embedded ? 'dark:text-white' : 'p-6 dark:text-white'">
    <!-- Pillar Generation Notice -->
    <div
      v-if="showPillarNotice"
      class="bg-amber-100 border border-amber-400 text-amber-800 px-4 py-3 rounded relative mb-4 flex items-center justify-between"
    >
      <div>
        <strong class="font-bold">Action Required:</strong>
        <span class="ml-1">
          Profiles have been added or updated. Click "Generate Pillar" to include them in your configuration.
        </span>
      </div>
      <button @click="showPillarNotice = false" class="text-amber-800 hover:text-amber-900">
        <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
        </svg>
      </button>
    </div>

    <!-- Header Section -->
    <div class="flex items-center justify-between mb-6">
      <h1 v-if="!embedded" class="text-3xl font-bold text-gray-900 dark:text-white">
        SNMP Metric Profiles - {{ filteredProfiles.length }}
        <span v-if="isFiltered" class="text-sm text-gray-500">(filtered)</span>
      </h1>
      <div v-else class="text-sm text-gray-500 dark:text-gray-400">
        {{ filteredProfiles.length }} profile{{ filteredProfiles.length === 1 ? '' : 's' }}
        <span v-if="isFiltered">(filtered)</span>
      </div>

      <div class="mb-4 text-base flex space-x-3 justify-between">
        <button
          v-if="canAddProfile || user?.is_staff"
          @click="generatePillar"
          :disabled="isGenerating"
          class="w-48 text-center bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition duration-200 disabled:opacity-50"
        >
          {{ isGenerating ? 'Generating...' : 'Generate Pillar' }}
        </button>
        <button
          v-if="canAddProfile || user?.is_staff"
          @click="triggerYAMLImport"
          :disabled="isImporting"
          class="w-48 text-center bg-purple-600 text-white px-6 py-2 rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 transition duration-200 disabled:opacity-50"
        >
          {{ isImporting ? 'Importing...' : 'Import from YAML' }}
        </button>
        <input
          ref="yamlFileInput"
          type="file"
          accept=".yaml,.yml,.sls"
          class="hidden"
          @change="handleYAMLImport"
        />
        <button
          v-if="canAddProfile || user?.is_staff"
          @click="$router.push({ name: 'AddSNMPProfile', query: tenant.addSchemaParam() })"
          class="w-48 text-center bg-blue-700 text-white px-6 py-2 rounded-md hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
        >
          Add Profile
        </button>
      </div>
    </div>

    <!-- Search and Filter Section -->
    <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
      <div class="mb-4 md:mb-0 md:flex-1 md:mr-4">
        <SearchBar v-model="searchQuery" placeholderText="Search profiles by name or description..." />
      </div>

      <div class="flex items-center mt-4 md:mt-0 space-x-2">
        <GlassSelect
          v-model="bulkAction"
          :options="bulkActionOptions"
          valueKey="value"
          labelKey="label"
          placeholder="Bulk Actions"
          class="!w-44"
        />
        <button
          @click="applyBulkAction"
          :disabled="!bulkAction || selectedProfiles.length === 0"
          :class="[
            'px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200',
            bulkAction && selectedProfiles.length
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed',
          ]"
        >
          Apply
        </button>
      </div>
    </div>

    <!-- Profiles Table -->
    <GenericTable
      ref="table"
      :columns="columns"
      :items="paginatedProfiles"
      itemKey="id"
      :selectable="true"
      @update:selectedItems="updateSelectedProfiles"
      @rowClick="goToProfileDetail"
    >
      <template #name="{ item }">
        <span class="font-medium">{{ item.name }}</span>
        <span v-if="!item.org" class="ml-2 px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded-full">
          Global
        </span>
      </template>

      <template #counts="{ item }">
        <div class="flex space-x-2 text-xs">
          <span class="bg-blue-100 text-blue-800 px-2 py-1 rounded">
            Metrics: {{ item.metrics_count }}
          </span>
          <span class="bg-green-100 text-green-800 px-2 py-1 rounded">
            Walks: {{ item.walks_count }}
          </span>
        </div>
      </template>

      <template #org_name="{ item }">
        {{ item.org_name || 'Global' }}
      </template>

      <template #last_modified="{ value }">
        {{ formatDateTime(value) }}
      </template>
    </GenericTable>

    <!-- Pagination -->
    <div class="flex flex-col md:flex-row items-center justify-between mt-8 space-y-4 md:space-y-0">
      <div class="flex items-center space-x-2">
        <button
          @click="changePage(-1)"
          :disabled="currentPage === 1"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 dark:text-white hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Previous
        </button>
        <button
          @click="changePage(1)"
          :disabled="currentPage === totalPages"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 dark:text-white hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Next
        </button>
      </div>
      <div class="text-gray-700 dark:text-white">Page {{ currentPage }} of {{ totalPages }}</div>
      <div>
        <GlassSelect
          v-model="itemsPerPage"
          :options="pageSizeOptions"
          valueKey="value"
          labelKey="label"
          placeholder="Page size"
          class="!w-36"
          @change="updatePagination"
        />
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="deletingItem">
      <div
        @click.self="deletingItem = false"
        class="items-center justify-center inset-0 absolute bg-gray-600 bg-opacity-50 flex z-10"
      >
        <div
          class="bg-white p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 dark:bg-gray-900 w-[65%] h-[35%] m-auto shadow-xl items-center text-center z-50"
        >
          <div class="mb-2 text-base flex space-x-2 justify-between">
            <span class="font-semibold">Are you sure you want to delete the following?</span>
            <button @click="deletingItem = false" class="hover:bg-white dark:hover:bg-gray-900">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
          <div
            class="overflow-auto w-[90%] h-[70%] m-auto items-center text-center mb-4 bg-gray-100 dark:bg-gray-800 rounded"
          >
            <div class="my-6">
              <p class="font-semibold justify-left flex pl-4">Profiles: {{ selectedProfiles.length }}</p>
              <p v-for="item in selectedProfiles" :key="item.id" class="font-medium justify-left flex pl-4">
                {{ item.name }}
              </p>
            </div>
          </div>
          <div class="text-base flex space-x-2 justify-center mt-6">
            <button
              class="rounded-md text-gray-500 dark:text-white dark:hover:bg-gray-900 hover:text-red-500 hover:bg-white focus:outline-none focus:ring-2 focus:ring-gray-500 transition duration-200"
              @click="confirmDelete"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M3 6h18" />
                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                <line x1="10" x2="10" y1="11" y2="17" />
                <line x1="14" x2="14" y1="11" y2="17" />
              </svg>
            </button>
            <button
              class="bg-blue-600 text-white px-6 py-1 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
              @click="deletingItem = false"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Success/Error Toast -->
    <div
      v-if="toastMessage"
      :class="[
        'fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg transition-opacity duration-300 z-50',
        toastType === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white',
      ]"
    >
      {{ toastMessage }}
    </div>
  </div>
</template>

<script>
import { getPersistedPageSize, setPersistedPageSize } from "@/composables/usePersistedPageSize";
import { getAllSNMPProfiles, deleteSNMPProfile, generateSNMPProfilesPillar, importSNMPProfilesFromYAML } from "@/services/snmpProfileService";
import SearchBar from "@/components/SearchBar.vue";
import GenericTable from "@/components/GenericTable.vue";
import PageError from "@/components/PageError.vue";
import { formatError } from "@/utils/errorMessages";
import { useStore } from "@/store";
import { useMultiTenant } from "@/composables/useMultiTenant";

import GlassSelect from "@/components/GlassSelect.vue";
export default {
  name: "SNMPProfileList",
  components: {
    SearchBar,
    GenericTable,
    PageError,
   GlassSelect,},
  props: {
    embedded: { type: Boolean, default: false },
  },
  setup() {
    const tenant = useMultiTenant();
    return { tenant };
  },
  data() {
    return {
      profiles: [],
      searchQuery: "",
      filteredProfiles: [],
      currentPage: 1,
      itemsPerPage: getPersistedPageSize(),
      selectedProfiles: [],
      deletingItem: false,
      bulkAction: "",
      isGenerating: false,
      isImporting: false,
      showPillarNotice: false,
      toastMessage: "",
      toastType: "success",
      columns: [
        { key: "name", label: "Name", sortable: true, width: "25%" },
        { key: "description", label: "Description", sortable: true, width: "25%" },
        { key: "counts", label: "Contents", sortable: false, width: "20%" },
        { key: "org_name", label: "Organisation", sortable: true, width: "15%" },
        { key: "last_modified", label: "Modified", sortable: true, width: "15%" },
      ],
      canAddProfile: false,
      canDeleteProfile: false,
      canViewProfile: false,
      error: "",
      errorCode: "",
    };
  },
  computed: {
    pageSizeOptions() {
      return [
        { value: 10, label: '10 per page' },
        { value: 20, label: '20 per page' },
        { value: 50, label: '50 per page' },
        { value: 100, label: '100 per page' },
        { value: -1, label: 'All' },
      ];
    },
    bulkActionOptions() {
      const opts = [];
      if (this.canDeleteProfile || this.user?.is_staff) {
        opts.push({ value: 'delete', label: 'Delete selected items' });
      }
      return opts;
    },

    user() {
      return useStore.getUser();
    },
    currentSchema() {
      return this.$route.query.schemaName || this.tenant.currentSchemaName?.value || null;
    },
    isFiltered() {
      return this.searchQuery !== "";
    },
    totalPages() {
      return this.itemsPerPage === -1 ? 1 : Math.ceil(this.filteredProfiles.length / this.itemsPerPage);
    },
    paginatedProfiles() {
      if (this.itemsPerPage === -1) {
        return this.filteredProfiles;
      }
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredProfiles.slice(start, end);
    },
  },
  watch: {
    searchQuery() {
      this.applyFilters();
    },
    user: {
      handler(user) {
        if (user) {
          this.checkPermissions();
        }
      },
      immediate: true,
    },
  },
  async mounted() {
    this.checkPermissions();
    await this.tenant.fetchOrganizations();
    if (this.embedded) {
      await this.fetchProfilesGlobal();
    } else {
      await this.tenant.loadInitialData(this.fetchProfiles);
    }
    // Check for pillar notice flag from AddSNMPProfile redirect
    if (this.$route.query.showPillarNotice) {
      this.showPillarNotice = true;
      // Clean up query param
      this.$router.replace({ query: {} });
    }
  },
  methods: {
    async checkPermissions() {
      if (this.user) {
        this.canAddProfile = await useStore.hasPermission("Can add SNMP Metric Profile");
        this.canDeleteProfile = await useStore.hasPermission("Can delete SNMP Metric Profile");
        this.canViewProfile = await useStore.hasPermission("Can view SNMP Metric Profile");
      }
    },
    async fetchProfiles(schemaName = null) {
      try {
        const profiles = await getAllSNMPProfiles(schemaName);
        this.profiles = [...profiles];
        this.applyFilters();
      } catch (error) {
        console.error("Error fetching SNMP profiles:", error);
        const { code, message } = formatError(error);
        this.errorCode = code;
        this.error = message;
      }
    },
    async fetchProfilesGlobal() {
      // Iterate every tenant schema and concat results so the Organisations
      // tab shows all profiles across the org tree. Each row is tagged with
      // org_name (from the fetching org) so the table's "Organisation"
      // column populates without a separate join.
      const orgs = this.tenant.organizations.value || [];
      const all = [];
      for (const org of orgs) {
        if (!org.schema_name) continue;
        try {
          const rows = await getAllSNMPProfiles(org.schema_name);
          for (const r of (rows || [])) {
            all.push({ ...r, org_name: r.org_name || org.name, _orgSchema: org.schema_name });
          }
        } catch (e) {
          console.warn(`SNMP profile fetch failed for schema=${org.schema_name}:`, e);
        }
      }
      this.profiles = all;
      this.applyFilters();
    },
    goToProfileDetail(profile) {
      this.$router.push({
        name: "SNMPProfileDetails",
        params: { id: profile.id },
        query: this.tenant.addSchemaParam(),
      });
    },
    updateSelectedProfiles(selectedItems) {
      this.selectedProfiles = selectedItems;
    },
    async applyBulkAction() {
      if (this.bulkAction === "delete") {
        this.deletingItem = true;
      }
      this.bulkAction = "";
    },
    async confirmDelete() {
      await this.deleteSelectedProfiles();
      this.deletingItem = false;
      this.bulkAction = "";
    },
    async deleteSelectedProfiles() {
      if (this.selectedProfiles.length === 0) {
        this.showToast("No profiles selected for deletion", "error");
        return;
      }
      try {
        const deletePromises = this.selectedProfiles.map((profile) => deleteSNMPProfile(profile.id, this.currentSchema));
        await Promise.all(deletePromises);
        this.showToast(`Deleted ${this.selectedProfiles.length} profile(s)`, "success");
        this.fetchProfiles(this.currentSchema);
        this.$refs.table.clearSelection();
        this.applyFilters();
        this.selectedProfiles = [];
      } catch (error) {
        console.error("Error during bulk delete:", error);
        this.showToast("An error occurred while deleting profiles", "error");
      }
    },
    async generatePillar() {
      this.isGenerating = true;
      try {
        const result = await generateSNMPProfilesPillar(this.currentSchema);
        this.showToast(`Generated pillar with ${result.profiles_count} profiles`, "success");
        // Hide pillar notice after successful generation
        this.showPillarNotice = false;
      } catch (error) {
        console.error("Error generating pillar:", error);
        this.showToast("Failed to generate pillar file", "error");
      } finally {
        this.isGenerating = false;
      }
    },
    triggerYAMLImport() {
      this.$refs.yamlFileInput.click();
    },
    async handleYAMLImport(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.isImporting = true;
      try {
        const result = await importSNMPProfilesFromYAML(file, this.currentSchema);
        const created = result.created_count || 0;
        const updated = result.updated_count || 0;
        let message = `Imported ${created} new, ${updated} updated profiles`;
        if (result.errors && result.errors.length > 0) {
          message += ` (${result.errors.length} errors)`;
          this.showToast(message, "error");
        } else {
          this.showToast(message, "success");
          // Show pillar notice after successful import
          if (created > 0 || updated > 0) {
            this.showPillarNotice = true;
          }
        }
        this.fetchProfiles(this.currentSchema);
      } catch (error) {
        console.error("Error importing YAML:", error);
        const errorMsg = error.response?.data?.error || "Failed to import YAML file";
        this.showToast(errorMsg, "error");
      } finally {
        this.isImporting = false;
        this.$refs.yamlFileInput.value = "";
      }
    },
    showToast(message, type = "success") {
      this.toastMessage = message;
      this.toastType = type;
      setTimeout(() => {
        this.toastMessage = "";
      }, 3000);
    },
    applyFilters() {
      const query = (this.searchQuery || "").toLowerCase();
      this.filteredProfiles = (this.profiles || []).filter((profile) => {
        const name = (profile?.name || "").toLowerCase();
        const description = (profile?.description || "").toLowerCase();
        return name.includes(query) || description.includes(query);
      });
      this.currentPage = 1;
    },
    changePage(delta) {
      this.currentPage += delta;
    },
    updatePagination() {
      this.currentPage = 1;
      setPersistedPageSize(this.itemsPerPage);
    },
    formatDateTime(dateString) {
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    },
  },
};
</script>
